import { Form } from "antd";
import { AddIcon } from "assets/icons";
import Button from "components/BaseWidgets/button";
import DatePicker from "components/BaseWidgets/datePicker";
import Divider from "components/BaseWidgets/divider";
import Input from "components/BaseWidgets/form/input";
import Select from "components/BaseWidgets/select";
import Tag from "components/BaseWidgets/tag";
import { useEffect, useState } from "react";
import "./style.scss";
import PickerModal from "../PickerModal";
import {
  useCommunities,
  useDistrict,
  useInterests,
  usePersonalities,
  useSelectOptions,
  useStates,
  useTown,
} from "hooks/api/useListing";
import { retrieveToken, storeToken } from "utils/storage";
import { formatDataWithIcons, mapToSelectOptions } from "utils/formatHelper";
import dayjs from "dayjs";

function AboutBride({ form }) {
  const { data: interestData, isLoading: isInterestPending } = useInterests();
  const { data: personalityData, isLoading: isPersonalityPending } =
    usePersonalities();
  const interestsData = formatDataWithIcons(interestData);
  const personalitiesData = formatDataWithIcons(personalityData);
  const [interestModal, setInterestModal] = useState(false);
  const [personalityModal, setPersonalityModal] = useState(false);
  const defaultCountry = { id: 104, name: "India" };
  const defaultState = { id: 13, name: "Kerala" };
  const userGender = JSON.parse(retrieveToken("user"));
  const [selectedInterests, setSelectedInterests] = useState([]);
  const [selectedPersonality, setSelectedPersonality] = useState([]);

  const formInterests = Form.useWatch("interests", form);
  const formPersonality = Form.useWatch("personality", form);

  useEffect(() => {
    if (Array.isArray(formInterests)) {
      setSelectedInterests(formInterests);
    }
  }, [formInterests]);

  useEffect(() => {
    if (Array.isArray(formPersonality)) {
      setSelectedPersonality(formPersonality);
    }
  }, [formPersonality]);

  const handleRemoveTag = (tagToRemove, type) => {
    if (type === "interests") {
      setSelectedInterests((prev) => prev.filter((tag) => tag !== tagToRemove));
    } else if (type === "personality") {
      setSelectedPersonality((prev) =>
        prev.filter((tag) => tag !== tagToRemove),
      );
    }
  };
  const findTag = (tagKey, data) => {
    for (const category of Object.values(data)) {
      const matchedTag = category.find((item) => item.key === tagKey);
      if (matchedTag) return matchedTag;
    }
    return null;
  };
  const renderSelectedTags = (tags, data, type) => {
    return tags.map((tagKey) => {
      const matchedTag = findTag(tagKey, data);
      const tagLabel = matchedTag?.label || tagKey?.toString() || "Unknown";

      return (
        <Tag
          key={tagKey}
          icon={
            <span className="selection-icon">
              {" "}
              <img src={matchedTag?.icon} />{" "}
            </span>
          }
          isActive={true}
          onClick={() => handleRemoveTag(tagKey, type)}
          closable={true}
        >
          {tagLabel}
        </Tag>
      );
    });
  };

  // select values
  const { religion, nationality } = useSelectOptions();
  const { data: communityData, isLoading: isCommunityPending } = useCommunities(
    {
      search: "",
      parent_id: Form.useWatch("religion", form),
    },
  );
  const selectCommunity = mapToSelectOptions(communityData);
  const { data: stateData, isLoading: isStatePending } = useStates({
    search: "",
    country_id: Form.useWatch("country", form) || defaultCountry.id,
  });
  const selectState = mapToSelectOptions(stateData);
  const { data: districtData, isLoading: isDistrictPending } = useDistrict({
    search: "",
    parent_id: Form.useWatch("city", form),
  });
  const selectDistrict = mapToSelectOptions(districtData);
  const { data: townData, isLoading: isTownPending } = useTown({
    search: "",
    place_id: Form.useWatch("place", form),
  });
  const selectTown = mapToSelectOptions(townData);

  useEffect(() => {
    if (
      !form.getFieldValue("country") &&
      nationality?.options?.length > 0 &&
      selectState?.length > 0
    ) {
      form.setFieldsValue({
        country: defaultCountry.id,
        city: defaultState.id,
        place: null,
        town: null,
        pin: null,
      });
    }
  }, [form, nationality?.options, selectState]);

  const minAge = userGender?.gender === "Female" ? 18 : 21;
  const cutoffDate = dayjs().subtract(minAge, "year").endOf("day");

  return (
    <>
      <div className="about-bride-wrapper flex flex-col items-center w-full">
        <div className="heading-wrapper w-full flex flex-col gap-2 mb-8">
          <p className="title font-title-xl">Basic info</p>
          <p className="sub-text font-body-md2">
            Let’s start by filling in some basic info
          </p>
        </div>
        <div className="basic-info-wrapper w-full">
          <div className="bride-form-container">
            <Form
              form={form}
              className="basic-form"
              layout="vertical"
              initialValues={{
                dob: cutoffDate,
              }}
            >
              <Form.Item
                name="dob"
                className="form-item m-0"
                rules={[
                  {
                    required: true,
                    message: "Please select your Date of Birth",
                  },
                  {
                    type: "object",
                    message: "Please select a valid date",
                  },
                ]}
              >
                <DatePicker
                  disabledDate={(current) => current && current > cutoffDate}
                  defaultValue={cutoffDate}
                  label="Date of Birth"
                />
              </Form.Item>

              <Form.Item
                name="religion"
                className="form-item m-0"
                rules={[
                  {
                    required: true,
                    message: "Please select a religion",
                  },
                ]}
              >
                <Select
                  widthVariant="wFull"
                  labelClassName={`label`}
                  inputClassName={`select-dropdown`}
                  label="Religion"
                  options={religion?.options}
                  onChange={(value) => {
                    form.setFieldsValue({
                      religion: value,
                      sub_religion: null,
                    });
                  }}
                  placeholder="Select"
                  isLoading={religion?.isLoading}
                />
              </Form.Item>

              <Form.Item
                name="sub_religion"
                className="form-item m-0"
                rules={[
                  {
                    required: true,
                    message: "Please select a community",
                  },
                ]}
              >
                <Select
                  isDisabled={!Form.useWatch("religion", form)}
                  widthVariant="wFull"
                  placeholder="Select"
                  labelClassName={`label`}
                  inputClassName={`select-dropdown`}
                  label="Community"
                  options={selectCommunity}
                  isLoading={isCommunityPending}
                  onChange={(value) => {
                    form.setFieldsValue({
                      sub_religion: value,
                    });
                  }}
                />
              </Form.Item>

              <Form.Item
                name="country"
                className="form-item m-0"
                rules={[
                  {
                    required: true,
                    message: "Please select a nationality",
                  },
                ]}
              >
                <Select
                  widthVariant="wFull"
                  placeholder="Select"
                  labelClassName={`label`}
                  inputClassName={`select-dropdown`}
                  label="Nationality"
                  options={nationality?.options}
                  isLoading={nationality?.isLoading}
                  onChange={(value) => {
                    form.setFieldsValue({
                      country: value,
                      city: null,
                      place: null,
                      town: null,
                      pin: null,
                    });
                  }}
                />
              </Form.Item>

              <Form.Item
                name="city"
                className="form-item m-0"
                rules={[
                  {
                    required: true,
                    message: "Please select a state",
                  },
                ]}
              >
                <Select
                  isDisabled={!Form.useWatch("country", form)}
                  widthVariant="wFull"
                  placeholder="Select"
                  labelClassName={`label`}
                  inputClassName={`select-dropdown`}
                  label="State"
                  options={selectState}
                  isLoading={isStatePending}
                  onChange={(value) => {
                    form.setFieldsValue({
                      city: value,
                      place: null,
                      town: null,
                      pin: null,
                    });
                  }}
                />
              </Form.Item>

              <Form.Item
                name="place"
                className="form-item m-0"
                rules={[
                  {
                    required: true,
                    message: "Please select a district",
                  },
                ]}
              >
                <Select
                  isDisabled={!Form.useWatch("city", form)}
                  widthVariant="wFull"
                  placeholder="Select"
                  labelClassName={`label`}
                  inputClassName={`select-dropdown`}
                  label="District"
                  options={selectDistrict}
                  isLoading={isDistrictPending}
                  onChange={(value) => {
                    form.setFieldsValue({
                      place: value,
                      town: null,
                      pin: null,
                    });
                  }}
                />
              </Form.Item>

              <Form.Item
                name="town"
                className="form-item m-0"
                rules={[
                  {
                    required: true,
                    message: "Please select a town",
                  },
                ]}
              >
                <Select
                  isDisabled={!Form.useWatch("place", form)}
                  widthVariant="wFull"
                  placeholder="Select"
                  labelClassName={`label`}
                  inputClassName={`select-dropdown`}
                  label="Town"
                  options={selectTown}
                  isLoading={isTownPending}
                  onChange={(value) => {
                    form.setFieldsValue({
                      town: value,
                      pin: null,
                    });
                  }}
                />
              </Form.Item>

              <Form.Item
                name="pin"
                className="form-item m-0"
                rules={[
                  {
                    pattern: /^\d{6}$/,
                    message: "Pincode must be exactly 6 digits",
                  },
                ]}
              >
                <Input
                  isDisabled={!Form.useWatch("town", form)}
                  type="number"
                  maxLength={6}
                  id="pincode"
                  labelClass={`label`}
                  label="Pincode"
                  placeholder="Enter"
                  onKeyPress={(e) => {
                    if (e.target.value.length >= 6) {
                      e.preventDefault();
                    }
                  }}
                  onChange={(e) => {
                    if (e.target.value.length > 6) {
                      e.target.value = e.target.value.slice(0, 6);
                    }
                  }}
                />
              </Form.Item>

              {/* hidden input for validating interest and personality values using form */}
              <Form.Item name="interests" hidden>
                <Input />
              </Form.Item>

              <Form.Item name="personality" hidden>
                <Input />
              </Form.Item>
            </Form>
          </div>
        </div>
        <Divider className="content-divider" />
        <div className="heading-wrapper interests-heading-wrapper select-update-wrapper w-full flex flex-col">
          <p className="title font-title-xl">Interests</p>
          <p className="sub-text font-body-md2 mt-2">
            Add the {userGender?.gender == "Female" ? "Bride's" : "Groom's"}{" "}
            interests.
          </p>
          {selectedInterests.length <= 4 && (
            <Button
              isLoading={isInterestPending}
              onClick={() => setInterestModal(true)}
              className="open-modal-btn"
              variant="tertiary"
              icon={<AddIcon />}
            >
              Add your interests
            </Button>
          )}

          {selectedInterests.length > 0 && (
            <div className="selected-tags flex flex-wrap gap-2 mt-4">
              {renderSelectedTags(
                selectedInterests,
                interestsData,
                "interests",
              )}
            </div>
          )}
        </div>

        <Divider className="content-divider" />

        <div className="heading-wrapper personality-heading-wrapper select-update-wrapper w-full flex flex-col">
          <p className="title font-title-xl">Personality</p>
          <p className="sub-text font-body-md2 mt-2">personality traits.</p>
          {selectedPersonality.length <= 4 && (
            <Button
              isLoading={isPersonalityPending}
              onClick={() => setPersonalityModal(true)}
              className="open-modal-btn"
              variant="tertiary"
              icon={<AddIcon />}
            >
              Add your personality
            </Button>
          )}
          {selectedPersonality.length > 0 && (
            <div className="selected-tags flex flex-wrap gap-2 mt-4">
              {renderSelectedTags(
                selectedPersonality,
                personalitiesData,
                "personality",
              )}
            </div>
          )}
        </div>
      </div>

      {/* Modal's */}
      <PickerModal
        open={interestModal}
        onCancel={() => setInterestModal(false)}
        onOk={(selectedTags) => {
          // setSelectedInterests(selectedTags);
          form.setFieldsValue({ interests: selectedTags });
          setInterestModal(false);
        }}
        title="Select your Interests"
        description="Choose the interests to help finding the perfect matches."
        selectedTags={selectedInterests}
        data={interestsData}
        popup="interests"
      />

      <PickerModal
        open={personalityModal}
        onCancel={() => setPersonalityModal(false)}
        selectedTags={selectedPersonality}
        onOk={(selectedTags) => {
          // setSelectedPersonality(selectedTags);
          form.setFieldsValue({ personality: selectedTags });
          setPersonalityModal(false);
        }}
        title="Select your personality"
        description="Choose the personality to help finding the perfect matches."
        data={personalitiesData}
        popup="personality"
      />
    </>
  );
}

export default AboutBride;
